import { z } from 'zod';

export const paginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});

export type Pagination = z.infer<typeof paginationSchema>;

export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const apiSuccessSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export type ApiSuccess = z.infer<typeof apiSuccessSchema>;

// Helper to transform 'null' string to actual null
const nullableUuidParam = z
  .string()
  .optional()
  .transform((val) => {
    if (val === undefined || val === '') return undefined;
    if (val === 'null') return null;
    return val;
  })
  .pipe(z.string().uuid().nullable().optional());

// Helper to properly parse boolean query params (z.coerce.boolean treats any non-empty string as true)
const booleanQueryParam = z
  .string()
  .optional()
  .transform((val) => {
    if (val === undefined || val === '') return undefined;
    return val === 'true';
  });

export const listFilesQuerySchema = z.object({
  folderId: nullableUuidParam,
  search: z.string().max(100).optional(),
  starred: booleanQueryParam,
  trashed: booleanQueryParam,
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sortBy: z.enum(['name', 'size', 'createdAt', 'updatedAt']).default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type ListFilesQuery = z.infer<typeof listFilesQuerySchema>;

export const listFoldersQuerySchema = z.object({
  parentId: nullableUuidParam,
  clientId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
});

export type ListFoldersQuery = z.infer<typeof listFoldersQuerySchema>;
